import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import type {
  AdminVapiToolRegistrySyncResponse,
  AdminVoicePhoneNumber,
  AdminVoicePhoneNumbersParams,
  AdminVoicePhoneSyncResponse,
} from "../../api/admin";
import {
  useAdminVapiToolRegistryDebug,
  useAdminVoicePhoneNumbers,
  useCutoverAdminVoicePhoneRouting,
  useInspectAdminVoicePhoneRouting,
  useRollbackAdminVoicePhoneRouting,
  useSyncAdminVapiTools,
  useSyncAdminTwilioPhoneNumbers,
  useSyncAdminVapiPhoneNumbers,
  useVerifyAdminVoicePhoneRouting,
} from "../../hooks/useVoicePhoneNumbers";
import VoiceNumberAssignDialog from "./VoiceNumberAssignDialog";
import VoiceNumberDetailDrawer from "./VoiceNumberDetailDrawer";
import VoiceNumberReleaseDialog from "./VoiceNumberReleaseDialog";

const statusOptions = [
  "available",
  "reserved",
  "assigned",
  "cooldown",
  "provider_error",
  "retired",
];

const connectionOptions = [
  "not_imported",
  "imported",
  "attached",
  "attach_failed",
  "import_pending",
  "attach_pending",
];

function label(value: string | null | undefined) {
  if (!value) return "not set";
  return value.replaceAll("_", " ");
}

function statusChip(value: string, kind: "inventory" | "connection" | "routing" = "inventory") {
  const palette: Record<string, { bg: string; color: string }> = {
    available: { bg: "#F0FDF4", color: "#16A34A" },
    assigned: { bg: "#EFF6FF", color: "#2563EB" },
    cooldown: { bg: "#FFFBEB", color: "#B45309" },
    reserved: { bg: "#F8FAFC", color: "#475569" },
    provider_error: { bg: "#FEF2F2", color: "#DC2626" },
    retired: { bg: "#F1F5F9", color: "#64748B" },
    attached: { bg: "#F0FDF4", color: "#16A34A" },
    imported: { bg: "#EFF6FF", color: "#2563EB" },
    not_imported: { bg: "#F8FAFC", color: "#64748B" },
    attach_failed: { bg: "#FEF2F2", color: "#DC2626" },
    import_pending: { bg: "#FFFBEB", color: "#B45309" },
    attach_pending: { bg: "#FFFBEB", color: "#B45309" },
    routed_to_vapi: { bg: "#F0FDF4", color: "#16A34A" },
    pending_verification: { bg: "#FFFBEB", color: "#B45309" },
    old_provider: { bg: "#FEF2F2", color: "#DC2626" },
    mismatch: { bg: "#FEF2F2", color: "#DC2626" },
    routing_failed: { bg: "#FEF2F2", color: "#DC2626" },
    rollback_failed: { bg: "#FEF2F2", color: "#DC2626" },
    unknown: { bg: "#F8FAFC", color: "#64748B" },
    synced: { bg: "#F0FDF4", color: "#16A34A" },
    stale: { bg: "#FFFBEB", color: "#B45309" },
    sync_error: { bg: "#FEF2F2", color: "#DC2626" },
    not_configured: { bg: "#F8FAFC", color: "#64748B" },
  };
  const color = palette[value] ?? { bg: "#F8FAFC", color: "#64748B" };
  return (
    <Chip
      label={label(value)}
      size="small"
      sx={{
        backgroundColor: color.bg,
        color: color.color,
        fontSize: kind !== "inventory" ? "0.68rem" : "0.72rem",
        fontWeight: 700,
        textTransform: "capitalize",
      }}
    />
  );
}

function syncSummary(result: AdminVoicePhoneSyncResponse | undefined) {
  if (!result) return null;
  const parts = [
    `${result.created} created`,
    `${result.updated} updated`,
    typeof result.retired === "number" ? `${result.retired} retired` : null,
  ].filter(Boolean);
  return `${result.provider}: ${parts.join(", ")}`;
}

function toolSyncSummary(result: AdminVapiToolRegistrySyncResponse | undefined) {
  if (!result) return null;
  return [
    `${result.created.length} created`,
    `${result.updated.length} updated`,
    `${result.unchanged.length} unchanged`,
    `${result.failed.length} failed`,
  ].join(", ");
}

function isAssignable(number: AdminVoicePhoneNumber) {
  return ["available", "cooldown", "reserved"].includes(number.status);
}

function canRelease(number: AdminVoicePhoneNumber) {
  return number.status === "assigned";
}

function needsRegionWarning(number: AdminVoicePhoneNumber) {
  return number.country_code === "AU" && (!number.region || number.region === "unknown");
}

function hasPreviousRouting(number: AdminVoicePhoneNumber) {
  return Boolean(number.previous_routing && Object.keys(number.previous_routing).length > 0);
}

function formatDate(value: string | null | undefined) {
  if (!value) return "none";
  return new Date(value).toLocaleString();
}

export default function AdminVoiceNumbersPage() {
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("");
  const [assigned, setAssigned] = useState("");
  const [selected, setSelected] = useState<AdminVoicePhoneNumber | null>(null);
  const [assignNumber, setAssignNumber] = useState<AdminVoicePhoneNumber | null>(null);
  const [releaseNumber, setReleaseNumber] = useState<AdminVoicePhoneNumber | null>(null);

  const params = useMemo<AdminVoicePhoneNumbersParams>(
    () => ({
      q: q.trim() || undefined,
      status: status || undefined,
      vapi_connection_status: connectionStatus || undefined,
      assigned: assigned === "" ? null : assigned === "true",
      limit: pageSize,
      offset: page * pageSize,
    }),
    [assigned, connectionStatus, page, pageSize, q, status]
  );

  const numbersQuery = useAdminVoicePhoneNumbers(params);
  const vapiToolsQuery = useAdminVapiToolRegistryDebug();
  const syncTools = useSyncAdminVapiTools();
  const syncTwilio = useSyncAdminTwilioPhoneNumbers();
  const syncVapi = useSyncAdminVapiPhoneNumbers();
  const inspectRouting = useInspectAdminVoicePhoneRouting();
  const cutoverRouting = useCutoverAdminVoicePhoneRouting();
  const verifyRouting = useVerifyAdminVoicePhoneRouting();
  const rollbackRouting = useRollbackAdminVoicePhoneRouting();
  const numbers = numbersQuery.data?.items ?? [];
  const total = numbersQuery.data?.pagination.total ?? 0;
  const vapiTools = vapiToolsQuery.data;
  const toolServerUrls = Array.from(
    new Set(
      (vapiTools?.tools ?? [])
        .map((tool) => tool.server_url)
        .filter((value): value is string => Boolean(value))
    )
  );
  const toolsComplete =
    Boolean(vapiTools) &&
    vapiTools?.configured_tool_count === vapiTools?.required_tool_count &&
    vapiTools?.missing_tools.length === 0 &&
    vapiTools?.stale_tools.length === 0 &&
    vapiTools?.errored_tools.length === 0;
  const syncing = syncTwilio.isPending || syncVapi.isPending;
  const syncingTools = syncTools.isPending;
  const routingBusy =
    inspectRouting.isPending ||
    cutoverRouting.isPending ||
    verifyRouting.isPending ||
    rollbackRouting.isPending;

  const refresh = () => numbersQuery.refetch();

  const handleCutover = (number: AdminVoicePhoneNumber) => {
    let confirmRegionWarning = false;
    if (needsRegionWarning(number)) {
      confirmRegionWarning = window.confirm(
        "AU number region could not be verified. Continue with routing cutover?"
      );
      if (!confirmRegionWarning) return;
    }
    cutoverRouting.mutate({
      voicePhoneNumberId: number.voice_phone_number_id,
      confirmRegionWarning,
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhoneInTalkRoundedIcon sx={{ color: "#C9734F", fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0F172A" }}>
            Voice numbers
          </Typography>
          <Chip label={`${total} total`} size="small" sx={{ backgroundColor: "#F1F5F9", color: "#64748B" }} />
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            disabled={syncing}
            onClick={() => syncTwilio.mutate(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Sync Twilio inventory
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            disabled={syncing}
            onClick={() => syncVapi.mutate(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Sync Vapi inventory
          </Button>
        </Stack>
      </Box>

      {(syncTwilio.data || syncVapi.data) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {[syncSummary(syncTwilio.data), syncSummary(syncVapi.data)].filter(Boolean).join(" · ")}
        </Alert>
      )}
      {(syncTwilio.isError || syncVapi.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Provider sync failed. Inventory was not changed locally.
        </Alert>
      )}
      {[inspectRouting.data, cutoverRouting.data, verifyRouting.data, rollbackRouting.data]
        .filter((item) => item && !item.ok && item.safe_error)
        .slice(-1)
        .map((item) => (
          <Alert key={item?.voice_phone_number_id ?? "routing-error"} severity="warning" sx={{ mb: 2 }}>
            {item?.safe_error}
          </Alert>
        ))}

      <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "12px", mb: 2, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                Vapi tools
              </Typography>
              {vapiToolsQuery.isLoading ? (
                <Chip label="Loading" size="small" sx={{ backgroundColor: "#F8FAFC", color: "#64748B", fontWeight: 700 }} />
              ) : vapiToolsQuery.isError ? (
                <Chip label="Error" size="small" sx={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 700 }} />
              ) : toolsComplete ? (
                <Chip label="Synced" size="small" sx={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 700 }} />
              ) : (
                <Chip label="Needs attention" size="small" sx={{ backgroundColor: "#FFFBEB", color: "#B45309", fontWeight: 700 }} />
              )}
            </Stack>
            <Typography sx={{ color: "#64748B", fontSize: "0.82rem" }}>
              Shared Vapi tool registry for this environment. Business assistants should link to these tool IDs after force sync.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={vapiToolsQuery.isFetching || syncingTools}
              onClick={() => vapiToolsQuery.refetch()}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Refresh status
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={syncingTools}
              onClick={() => syncTools.mutate(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Sync tools
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={syncingTools}
              onClick={() => syncTools.mutate(true)}
              sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#C9734F", "&:hover": { backgroundColor: "#B86342" } }}
            >
              Force sync tools
            </Button>
          </Stack>
        </Box>

        {syncTools.data && (
          <Alert severity={syncTools.data.ok ? "info" : "warning"} sx={{ mt: 2 }}>
            Vapi tools: {toolSyncSummary(syncTools.data)}
            {syncTools.data.sync_error ? ` · ${syncTools.data.sync_error}` : ""}
          </Alert>
        )}
        {syncTools.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Vapi tool sync failed. Check provider credentials and server URL.
          </Alert>
        )}
        {vapiToolsQuery.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load Vapi tool registry status.
          </Alert>
        )}

        {vapiTools && (
          <>
            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
              <Box>
                <Typography sx={{ color: "#64748B", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase" }}>
                  Configured
                </Typography>
                <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                  {vapiTools.configured_tool_count} / {vapiTools.required_tool_count}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: "#64748B", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase" }}>
                  Missing
                </Typography>
                <Typography sx={{ color: vapiTools.missing_tools.length ? "#DC2626" : "#0F172A", fontWeight: 800 }}>
                  {vapiTools.missing_tools.length}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: "#64748B", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase" }}>
                  Errored
                </Typography>
                <Typography sx={{ color: vapiTools.errored_tools.length ? "#DC2626" : "#0F172A", fontWeight: 800 }}>
                  {vapiTools.errored_tools.length}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: "#64748B", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase" }}>
                  Tool server URL
                </Typography>
                <Typography sx={{ color: "#0F172A", fontSize: "0.78rem", wordBreak: "break-all" }}>
                  {toolServerUrls.length === 1 ? toolServerUrls[0] : toolServerUrls.length > 1 ? "mixed URLs" : "not configured"}
                </Typography>
              </Box>
            </Box>

            <Box component="details" sx={{ mt: 2 }}>
              <Box component="summary" sx={{ cursor: "pointer", color: "#475569", fontWeight: 700, fontSize: "0.82rem" }}>
                Technical details
              </Box>
              <TableContainer sx={{ mt: 1, border: "1px solid #E2E8F0", borderRadius: "8px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Tool</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Server URL</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Tool ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Last synced</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748B" }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vapiTools.tools.map((tool) => (
                      <TableRow key={tool.tool_name}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#0F172A" }}>
                          {tool.tool_name}
                        </TableCell>
                        <TableCell>{statusChip(tool.sync_status)}</TableCell>
                        <TableCell sx={{ fontSize: "0.72rem", color: "#475569", wordBreak: "break-all", maxWidth: 300 }}>
                          {tool.server_url ?? "not configured"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.72rem", color: "#475569", wordBreak: "break-all", maxWidth: 180 }}>
                          {tool.vapi_tool_id ?? "not configured"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.72rem", color: "#475569" }}>
                          {formatDate(tool.last_synced_at)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.72rem", color: tool.sync_error ? "#DC2626" : "#64748B", maxWidth: 260 }}>
                          {tool.sync_error ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "12px", mb: 2, p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr 1fr" }, gap: 1.5 }}>
          <TextField
            label="Search number or business"
            value={q}
            onChange={(event) => {
              setPage(0);
              setQ(event.target.value);
            }}
            size="small"
          />
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setPage(0);
                setStatus(event.target.value);
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {statusOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {label(item)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Vapi status</InputLabel>
            <Select
              label="Vapi status"
              value={connectionStatus}
              onChange={(event) => {
                setPage(0);
                setConnectionStatus(event.target.value);
              }}
            >
              <MenuItem value="">All Vapi states</MenuItem>
              {connectionOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {label(item)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Assignment</InputLabel>
            <Select
              label="Assignment"
              value={assigned}
              onChange={(event) => {
                setPage(0);
                setAssigned(event.target.value);
              }}
            >
              <MenuItem value="">All numbers</MenuItem>
              <MenuItem value="true">Assigned</MenuItem>
              <MenuItem value="false">Unassigned</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {numbersQuery.isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress size={28} sx={{ color: "#C9734F" }} />
        </Box>
      )}

      {numbersQuery.isError && <Alert severity="error">Failed to load voice numbers.</Alert>}

      {!numbersQuery.isLoading && !numbersQuery.isError && (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "12px" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Number</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Vapi</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Routing</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Region</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Assigned business</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Warning</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.76rem", color: "#64748B" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {numbers.map((number) => (
                <TableRow key={number.voice_phone_number_id} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#0F172A" }}>
                    {number.phone_number}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.78rem", color: "#64748B", textTransform: "capitalize" }}>
                    {label(number.provider)}
                  </TableCell>
                  <TableCell>{statusChip(number.status)}</TableCell>
                  <TableCell>{statusChip(number.vapi_connection_status, "connection")}</TableCell>
                  <TableCell>{statusChip(number.routing_status ?? "unknown", "routing")}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem", color: "#64748B" }}>
                    {[number.country_code, number.region].filter(Boolean).join(" / ") || "unknown"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.78rem", color: "#475569" }}>
                    {number.assigned_business_name ?? number.assigned_business_id ?? "Unassigned"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {number.safe_error ? (
                      <Tooltip title={number.safe_error}>
                        <Chip label="Error" color="error" size="small" />
                      </Tooltip>
                    ) : number.country_code === "AU" && (!number.region || number.region === "unknown") ? (
                      <Chip label="Region unknown" color="warning" size="small" />
                    ) : number.cooldown_until ? (
                      <Tooltip title={formatDate(number.cooldown_until)}>
                        <Chip label="Cooldown" color="warning" size="small" />
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75}>
                      <Button size="small" variant="text" onClick={() => setSelected(number)} sx={{ textTransform: "none", fontWeight: 700 }}>
                        Details
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        disabled={routingBusy}
                        onClick={() => inspectRouting.mutate(number.voice_phone_number_id)}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Inspect
                      </Button>
                      {isAssignable(number) && (
                        <Button size="small" variant="outlined" onClick={() => setAssignNumber(number)} sx={{ textTransform: "none", fontWeight: 700 }}>
                          Assign
                        </Button>
                      )}
                      {number.status === "assigned" && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={routingBusy}
                            onClick={() => handleCutover(number)}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Cut over
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            disabled={routingBusy}
                            onClick={() => verifyRouting.mutate(number.voice_phone_number_id)}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Verify
                          </Button>
                        </>
                      )}
                      {hasPreviousRouting(number) && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          disabled={routingBusy}
                          onClick={() =>
                            rollbackRouting.mutate({
                              voicePhoneNumberId: number.voice_phone_number_id,
                              reason: "Admin rollback from portal",
                            })
                          }
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Roll back
                        </Button>
                      )}
                      {canRelease(number) && (
                        <Button size="small" color="error" variant="outlined" onClick={() => setReleaseNumber(number)} sx={{ textTransform: "none", fontWeight: 700 }}>
                          Release
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {numbers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 4, color: "#94A3B8", fontSize: "0.84rem" }}>
                    No voice numbers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onPageChange={(_, value) => setPage(value)}
            sx={{ borderTop: "1px solid #E2E8F0" }}
          />
        </TableContainer>
      )}

      <VoiceNumberDetailDrawer number={selected} onClose={() => setSelected(null)} />
      <VoiceNumberAssignDialog
        number={assignNumber}
        onClose={() => setAssignNumber(null)}
        onAssigned={refresh}
      />
      <VoiceNumberReleaseDialog
        number={releaseNumber}
        onClose={() => setReleaseNumber(null)}
        onReleased={refresh}
      />
    </Box>
  );
}
